import { CommonModule } from '@angular/common';
import { Component, OnInit, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { RippleModule } from 'primeng/ripple';
import { AuthService } from '../../shared/services/auth.service';
import { ThemeService } from '../../shared/services/theme.service';
import { environment } from '../../../environments/environment';

export type AuthRole = 'candidate' | 'panel' | 'corporates';

interface RoleInfo {
  key: AuthRole;
  displayName: string;
  tagline: string;
  description: string;
  icon: string;
  accentClass: string;
}

@Component({
  selector: 'app-register',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterModule, ButtonModule, InputTextModule, RippleModule],
  templateUrl: './register.component.html',
  styleUrl: './register.component.scss',
})
export class RegisterComponent implements OnInit {
  private authService = inject(AuthService);
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private themeService = inject(ThemeService);

  email = '';
  loading = signal(false);
  errorMsg = signal('');
  isForgotPassword = signal(false);
  role = signal<AuthRole>('candidate');

  readonly isDark = this.themeService.isDark;

  readonly roleInfoMap: Record<AuthRole, RoleInfo> = {
    candidate: {
      key: 'candidate',
      displayName: 'Candidate',
      tagline: 'Start Your Journey',
      description: 'Create your account and start practicing with real industry experts today.',
      icon: 'pi-briefcase',
      accentClass: 'auth-panel--candidate',
    },
    panel: {
      key: 'panel',
      displayName: 'Panelist',
      tagline: 'Join as an Expert',
      description: 'Register as an panelist and help shape the next generation of professionals.',
      icon: 'pi-star',
      accentClass: 'auth-panel--panel',
    },
    corporates: {
      key: 'corporates',
      displayName: 'Employer',
      tagline: 'Transform Your Hiring',
      description: 'Sign up to access structured, expert-led interviews for your organisation.',
      icon: 'pi-building',
      accentClass: 'auth-panel--corporate',
    },
  };

  get currentRole(): RoleInfo {
    return this.roleInfoMap[this.role()];
  }

  /** Panelist/Employer sign-ups use a work email; candidates can use any email. */
  get emailPlaceholder(): string {
    return this.role() === 'candidate' ? 'you@example.com' : 'Enter your work email';
  }

  ngOnInit(): void {
    const flow = localStorage.getItem('authFlow');
    this.isForgotPassword.set(flow === 'forgotPassword');

    const stored = localStorage.getItem('ip-auth-role') as AuthRole | null;
    if (stored && this.roleInfoMap[stored]) {
      this.role.set(stored);
    }

    // Referral link (see candidateReferralShare()): ?ref=CODE arrives here
    // first, stash it so CompleteProfileComponent can pre-fill the
    // "Referral Code" field a few steps later in the flow.
    const ref = this.route.snapshot.queryParamMap.get('ref');
    if (ref) {
      localStorage.setItem('ip-referral-code', ref);
    }
  }

  toggleTheme(): void {
    this.themeService.toggle();
  }

  onSubmit(): void {
    if (!this.email) {
      this.errorMsg.set('Please enter your email address.');
      return;
    }
    this.loading.set(true);
    this.errorMsg.set('');

    // For sign-up, check if email is already registered before sending OTP
    if (!this.isForgotPassword()) {
      this.authService.checkEmailExists(this.email).subscribe({
        next: ({ exists }) => {
          if (exists) {
            this.loading.set(false);
            this.errorMsg.set('An account with this email already exists. Please log in instead.');
            return;
          }
          this.sendOtp();
        },
        error: () => {
          // If the check itself fails, proceed so users aren't blocked
          this.sendOtp();
        },
      });
    } else {
      this.sendOtp();
    }
  }

  private sendOtp(): void {
    localStorage.setItem('verifyEmail', this.email);
    localStorage.setItem('ip-auth-role', this.role());

    const purpose = this.isForgotPassword() ? 'forgot-password' : 'register';
    this.authService.generateOtp(this.email, purpose).subscribe({
      next: (res) => {
        this.loading.set(false);
        // EmailOtpController#generateOtp always returns HTTP 200, a
        // rejected "no account found" case (forgot-password only) arrives
        // here via res.success === false, not the error channel.
        if (!res.success) {
          this.errorMsg.set(res.message || 'No account found with this email.');
          return;
        }
        this.router.navigate(['/auth/verify-otp']);
      },
      error: (err) => {
        console.error('OTP generation failed', err);
        this.loading.set(false);
        this.errorMsg.set(this._extractError(err, 'Failed to send OTP. Please try again.'));
      },
    });
  }

  /**
   * True when errorMsg() is the "no account found" rejection from the
   * forgot-password OTP check (EmailOtpController#generateOtp), lets the
   * template render "register" as a clickable inline link instead of plain
   * text.
   */
  showRegisterCta(): boolean {
    return this.isForgotPassword() && /register first/i.test(this.errorMsg());
  }

  /** Splits errorMsg() around the word "register" for the inline-link template. */
  errorParts(): { text: string; isLink: boolean }[] {
    return this.errorMsg()
      .split(/(register)/i)
      .filter((part) => part.length > 0)
      .map((part) => ({ text: part, isLink: /^register$/i.test(part) }));
  }

  /**
   * Clicking the inline "register" link switches this same page out of
   * forgot-password mode into normal sign-up mode (same role tab, same
   * email already typed), no navigation needed since /auth/register is
   * already the route we're on.
   */
  switchToRegister(): void {
    this.isForgotPassword.set(false);
    this.errorMsg.set('');
    localStorage.removeItem('authFlow');
  }

  /** Backend returns { message } (JSON) for ResponseStatusException-based rejections. */
  private _extractError(err: any, fallback: string): string {
    let body = err?.error;
    if (typeof body === 'string') {
      try {
        body = JSON.parse(body);
      } catch {
        return body.trim() || fallback;
      }
    }
    return body?.message || body?.error || fallback;
  }

  loginWithGoogle(): void {
    localStorage.setItem('ip-auth-role', this.role());
    const google = (window as any).google;
    if (!google?.accounts?.id) {
      this.errorMsg.set('Google Sign-In is unavailable. Please try again shortly.');
      return;
    }
    google.accounts.id.initialize({
      client_id: environment.googleClientId,
      callback: (response: { credential: string }) => {
        this._handleGoogleCredential(response.credential);
      },
      cancel_on_tap_outside: false,
    });
    google.accounts.id.prompt();
  }

  private _handleGoogleCredential(idToken: string): void {
    this.loading.set(true);
    this.errorMsg.set('');
    this.authService.googleAuth(idToken, this.role()).subscribe({
      next: (res) => {
        this.authService.storeSession(res);
        this.loading.set(false);
        this._redirectByRole();
      },
      error: (err) => {
        this.loading.set(false);
        this.errorMsg.set(this._extractError(err, 'Google sign-in failed. Please try again.'));
      },
    });
  }

  private _redirectByRole(): void {
    const role = this.role();
    if (role === 'panel') {
      this.router.navigate(['/app/panelist']);
    } else if (role === 'corporates') {
      this.router.navigate(['/app/employer']);
    } else {
      this.router.navigate(['/app/candidate']);
    }
  }

  goToLogin(): void {
    localStorage.setItem('ip-auth-role', this.role());
    localStorage.removeItem('authFlow');
    this.router.navigate(['/auth/login']);
  }
}
